import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ToolbarComponent } from '../../core/layout/components/toolbar/toolbar.component';
import { UserService } from '../../services/user/user.service';
import { IRoleOption } from '@shared/models';
import { GithubService } from '../services/github-service.service';
import { IUser } from '@shared/models';

@Component({
  selector: 'app-admin',
  imports: [CommonModule, ToolbarComponent, FormsModule, ReactiveFormsModule],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.scss',
})
export class AdminComponent implements OnInit {
  users: IUser[] = [];
  filteredUsers: IUser[] = [];
  searchTerm: string = '';
  showRoleDialog = false;
  showConfirmation = false;
  showAddUserDialog = false;
  showAddUserConfirmation = false;
  selectedUserIndex: number | null = null;
  selectedUser: IUser | null = null;
  newRoleType: string | null = null;
  selectedProjects: string[] = [];
  selectedProjectsForRoleChange: string[] = [];

  // Add User reactive form
  addUserForm!: FormGroup;
  projects: string[] = [];

  availableRoles: IRoleOption[] = [
    {
      type: 'admin',
      title: 'Admin',
      description:
        'Integer hendrerit, lorem sit amet cursus scelerisque, arcu maris luctus.',
      icon: 'assets/icons/admin.svg',
    },
    {
      type: 'editor',
      title: 'Editor',
      description: 'Sed suscipit, justo at molestie placerat, libero.',
      icon: 'assets/icons/editor.svg',
    },
    {
      type: 'viewer',
      title: 'Viewer',
      description: 'Aenean elementum, ante vitae porta bibendum risus.',
      icon: 'assets/icons/viewer.svg',
    },
    {
      type: 'viewer_restricted',
      title: 'Viewer Restricted',
      description: 'Duis vitae sem eget purus lacinia tristique.',
      icon: 'assets/icons/viewer-limited.svg',
    },
  ];

  // Contadores
  get adminCount(): number {
    return this.users.filter((u) => u.userType === 'admin').length;
  }

  get editorCount(): number {
    return this.users.filter((u) => u.userType === 'editor').length;
  }

  get viewersCount(): number {
    return this.users.filter((u) => u.userType === 'viewer').length;
  }

  get restrictedViewersCount(): number {
    return this.users.filter((u) => u.userType === 'viewer_restricted').length;
  }

  constructor(
    readonly _userService: UserService,
    readonly _githubService: GithubService,
    readonly fb: FormBuilder,
  ) {
    this.initAddUserForm();
  }

  ngOnInit(): void {
    this.loadUsers();
    this.getRepositories();
  }

  async loadUsers(): Promise<void> {
    try {
      this.users = await this._userService.fetchUsers();
      this.filteredUsers = [...this.users];
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }

  // Add User Methods
  initAddUserForm(): void {
    this.addUserForm = this.fb.group({
      prid: ['', Validators.required],
      name: ['', Validators.required],
      role: ['', Validators.required],
    });
  }

  addUser(): void {
    this.showAddUserDialog = true;
    this.showAddUserConfirmation = false;
  }

  closeAddUserDialog(): void {
    this.showAddUserDialog = false;
    this.showAddUserConfirmation = false;
    this.selectedProjects = [];
    this.resetAddUserForm();
    this.ngOnInit();
  }

  resetAddUserForm(): void {
    this.addUserForm.reset();
  }

  selectRoleForNewUser(roleType: string): void {
    this.addUserForm.patchValue({ role: roleType });
  }

  proceedToAddUserConfirmation(): void {
    if (this.addUserForm.valid) {
      this.showAddUserConfirmation = true;
    }
  }

  backToAddUserForm(): void {
    this.showAddUserConfirmation = false;
    this.selectedProjects = [];
  }

  onProjectCheckboxChange(event: Event, project: string): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      if (!this.selectedProjects.includes(project)) {
        this.selectedProjects.push(project);
      }
    } else {
      this.selectedProjects = this.selectedProjects.filter(
        (p) => p !== project,
      );
    }
  }

  async confirmAddUser(): Promise<void> {
    if (this.addUserForm.valid) {
      const formValue = this.addUserForm.value;

      // Validate projects for viewer_restricted
      if (
        formValue.role === 'viewer_restricted' &&
        this.selectedProjects.length === 0
      ) {
        return;
      }

      const newUser: any = {
        prid_id: formValue.prid,
        user_type: formValue.role,
        name: formValue.name,
        projects:
          formValue.role === 'viewer_restricted' ? this.selectedProjects : [],
      };

      try {
        await this._userService.createUser(newUser);
        this.users.push(newUser);
        this.onSearchChange();
        this.closeAddUserDialog();
      } catch (error) {
        console.error('Error creating user:', error);
      }
    }
  }

  get addUserFormValue() {
    return this.addUserForm.value;
  }

  onSearchChange(): void {
    const searchLower = this.searchTerm.toLowerCase().trim();

    if (!searchLower) {
      this.filteredUsers = [...this.users];
      return;
    }

    this.filteredUsers = this.users.filter((user) => {
      const pridMatch = user.pridId?.toLowerCase().includes(searchLower);
      const nameMatch = user.name?.toLowerCase().includes(searchLower);
      return pridMatch || nameMatch;
    });
  }

  openRoleDialog(index: number): void {
    this.selectedUser = this.filteredUsers[index];
    this.selectedUserIndex = this.users.findIndex(
      (u) => u.pridId === this.selectedUser?.pridId,
    );
    this.showRoleDialog = true;
  }

  closeRoleDialog(): void {
    this.showRoleDialog = false;
    this.showConfirmation = false;
    this.selectedUserIndex = null;
    this.selectedUser = null;
    this.newRoleType = null;
    this.selectedProjectsForRoleChange = [];
  }

  selectRole(roleType: string): void {
    if (this.selectedUserIndex !== null) {
      const currentRole = this.users[this.selectedUserIndex].userType;

      // If it's the same role, close without confirmation
      if (currentRole === roleType) {
        this.closeRoleDialog();
        return;
      }

      // If it's different, show confirmation
      this.newRoleType = roleType;
      this.showConfirmation = true;
    }
  }

  async confirmRoleChange(): Promise<void> {
    if (this.selectedUserIndex !== null && this.newRoleType) {
      // Validate projects for viewer_restricted
      if (
        this.newRoleType === 'viewer_restricted' &&
        this.selectedProjectsForRoleChange.length === 0
      ) {
        return;
      }

      const updatedUser: any = {
        prid_id: this.users[this.selectedUserIndex].pridId,
        user_type: this.newRoleType,
        projects:
          this.newRoleType === 'viewer_restricted'
            ? this.selectedProjectsForRoleChange
            : [],
      };

      try {
        await this._userService.updateUser(updatedUser);
        this.users[this.selectedUserIndex] = updatedUser;
        this.onSearchChange();
        this.closeRoleDialog();
      } catch (error) {
        console.error('Error updating user role:', error);
      }
    }
  }

  cancelRoleChange(): void {
    this.showConfirmation = false;
    this.newRoleType = null;
    this.selectedProjectsForRoleChange = [];
  }

  backToRoles(): void {
    this.showConfirmation = false;
    this.newRoleType = null;
    this.selectedProjectsForRoleChange = [];
  }

  onProjectCheckboxChangeForRole(event: Event, project: string): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      if (!this.selectedProjectsForRoleChange.includes(project)) {
        this.selectedProjectsForRoleChange.push(project);
      }
    } else {
      this.selectedProjectsForRoleChange =
        this.selectedProjectsForRoleChange.filter((p) => p !== project);
    }
  }

  getSelectedUser(): IUser | null {
    return this.selectedUser;
  }

  getRoleInfo(roleType: string): IRoleOption | undefined {
    return this.availableRoles.find((r) => r.type === roleType);
  }

  async getRepositories() {
    try {
      const response = await this._githubService.fetchProjects();
      this.projects = Object.keys(response.ec2Repositories);
    } catch (error) {
      console.error('Error fetching repositories:', error);
      throw error;
    }
  }
}
